import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  ArrowLeft, BookOpen, GraduationCap, Loader2, Mail,
  Phone, RefreshCw, UserRound, Clock,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { subjectsApi, teachersApi, classesApi, timetableApi } from '../api/services.js'
import Avatar from '../components/Avatar.jsx'
import PageHeader from '../components/PageHeader.jsx'

const getTeacherId = (item) => item?._id || item?.id || item
const getTeacherName = (item) => item?.name || `${item?.firstName || ''} ${item?.lastName || ''}`.trim() || 'Teacher'
const getSubjectClass = (subject) => subject?.classId || subject?.class || null
const getClassId = (item) => item?._id || item?.id || item
const getClassLabel = (item) => {
  if (!item) return 'No class assigned'
  if (typeof item === 'string') return item
  const name = item.name || item.className || 'Class'
  return item.section ? `${name} — ${item.section}` : name
}

export default function TeacherProfilePage() {
  const { teacherId } = useParams()
  const [teacher, setTeacher] = useState(null)
  const [subjects, setSubjects] = useState([])
  const [timetableSubjectIds, setTimetableSubjectIds] = useState(new Set())
  const [classes, setClasses] = useState([])
  const [loading, setLoading] = useState(true)

  const fetchData = async () => {
    try {
      setLoading(true)
      const [teacherData, subjectsData, classesData, periodsData] = await Promise.allSettled([
        teachersApi.getOne(teacherId),
        subjectsApi.getAll(),
        classesApi.getAll(),
        timetableApi.getByTeacher(teacherId),
      ])
      if (teacherData.status === 'fulfilled') setTeacher(teacherData.value)
      else { toast.error('Failed to load teacher profile'); setTeacher(null) }
      if (subjectsData.status === 'fulfilled') {
        setSubjects(Array.isArray(subjectsData.value) ? subjectsData.value : subjectsData.value?.subjects ?? [])
      }
      if (classesData.status === 'fulfilled') {
        setClasses(Array.isArray(classesData.value) ? classesData.value : classesData.value?.classes ?? [])
      }
      if (periodsData.status === 'fulfilled') {
        const periods = Array.isArray(periodsData.value) ? periodsData.value : periodsData.value?.periods ?? periodsData.value?.slots ?? []
        const ids = new Set(
          periods
            .map((p) => p.subjectId?._id || p.subjectId?.id || (typeof p.subjectId === 'string' ? p.subjectId : null))
            .filter(Boolean)
            .map(String)
        )
        setTimetableSubjectIds(ids)
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load teacher profile')
      setTeacher(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [teacherId])

  const assignedSubjects = useMemo(
    () =>
      subjects.filter((subject) => {
        const subjectId = String(subject._id || subject.id || '')
        const inTeachersArray = (subject.teachers || []).some(
          (t) => String(getTeacherId(t)) === String(teacherId)
        )
        const inTimetable = subjectId && timetableSubjectIds.has(subjectId)
        return inTeachersArray || inTimetable
      }),
    [subjects, teacherId, timetableSubjectIds]
  )

  const classSections = useMemo(
    () =>
      Array.from(
        new Map(
          assignedSubjects.map((subject) => {
            const classItem = getSubjectClass(subject)
            return [String(getClassId(classItem) || getClassLabel(classItem)), getClassLabel(classItem)]
          })
        ).values()
      ),
    [assignedSubjects]
  )

  const assignedAsClassTeacher = useMemo(() => {
    return classes.find((c) => {
      const ct = c.classTeacher
      if (!ct) return false
      const ctId = typeof ct === 'object' ? (ct._id || ct.id) : ct
      return String(ctId) === String(teacherId)
    }) || null
  }, [classes, teacherId])

  const assignedAsTempTeacher = useMemo(() => {
    return classes.filter((c) => {
      const tmp = c.tempClassTeacher
      if (!tmp?.teacherId) return false
      const tid = typeof tmp.teacherId === 'object' ? (tmp.teacherId._id || tmp.teacherId.id) : tmp.teacherId
      return String(tid) === String(teacherId)
    })
  }, [classes, teacherId])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-6 h-6 animate-spin text-gray-300" />
      </div>
    )
  }

  if (!teacher) {
    return (
      <div className="card p-8 text-center">
        <GraduationCap className="w-10 h-10 mx-auto text-gray-200 mb-3" />
        <h2 className="text-lg font-semibold text-gray-900">Teacher not found</h2>
        <p className="text-sm text-gray-500 mt-2">The requested teacher profile could not be loaded.</p>
        <Link to="/teachers" className="btn-secondary inline-flex items-center gap-2 mt-5">
          <ArrowLeft className="w-4 h-4" /> Back to Teachers
        </Link>
      </div>
    )
  }

  const displayName = getTeacherName(teacher)

  return (
    <div className="space-y-6">
      <PageHeader
        title={displayName}
        subtitle="Review contact details, subject assignments, and the class sections this teacher currently supports."
        chips={[
          teacher.role || 'teacher',
          teacher.gender || 'Gender not set',
          `${assignedSubjects.length} assigned subjects`,
        ]}
        actions={
          <div className="flex items-center gap-2">
            <button onClick={fetchData} className="btn-secondary p-2" title="Refresh profile">
              <RefreshCw className="w-4 h-4" />
            </button>
            <Link to="/teachers" className="btn-secondary inline-flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" /> Back to Teachers
            </Link>
          </div>
        }
      />

      <div className="grid xl:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.9fr)] gap-5">
        <div className="card p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <Avatar src={teacher.avatar} name={displayName} size="xl" />
            <div>
              <h3 className="text-2xl font-bold text-gray-900">{displayName}</h3>
              <p className="text-sm text-gray-500 mt-1">Teacher profile</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">{teacher.role || 'teacher'}</span>
                {teacher.gender && <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">{teacher.gender}</span>}
                {assignedAsClassTeacher && (
                  <span className="rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700">
                    Class Teacher
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">
                <Mail className="w-3.5 h-3.5" /> Email
              </div>
              <p className="text-sm font-medium text-gray-900 mt-2 break-all">{teacher.email || 'Not provided'}</p>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">
                <Phone className="w-3.5 h-3.5" /> Mobile
              </div>
              <p className="text-sm font-medium text-gray-900 mt-2">{teacher.mobile || 'Not provided'}</p>
            </div>
          </div>
        </div>

        <div className="grid gap-4">
          {/* Class Teacher Assignment */}
          <div className={`card p-5 ${assignedAsClassTeacher ? 'border-primary-200 bg-primary-50/30' : ''}`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${assignedAsClassTeacher ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-400'}`}>
                <GraduationCap className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-500">Class Teacher Of</p>
                {assignedAsClassTeacher ? (
                  <Link
                    to={`/classes/${assignedAsClassTeacher._id || assignedAsClassTeacher.id}`}
                    className="text-lg font-bold text-primary-700 hover:text-primary-800 truncate block"
                  >
                    {assignedAsClassTeacher.name}
                    {assignedAsClassTeacher.section ? ` — ${assignedAsClassTeacher.section}` : ''}
                  </Link>
                ) : (
                  <p className="text-base font-semibold text-gray-400">Not assigned</p>
                )}
              </div>
            </div>
            {assignedAsTempTeacher.length > 0 && (
              <div className="mt-4 pt-4 border-t border-primary-100 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-600 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Temporary Assignments
                </p>
                {assignedAsTempTeacher.map((c) => {
                  const tmp = c.tempClassTeacher
                  const cId = c._id || c.id
                  const from = tmp?.from ? new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short' }).format(new Date(tmp.from)) : ''
                  const to = tmp?.to ? new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(tmp.to)) : ''
                  return (
                    <div key={cId} className="flex items-center justify-between gap-2">
                      <Link to={`/classes/${cId}`} className="text-sm font-semibold text-amber-700 hover:text-amber-800">
                        {c.name}{c.section ? ` — ${c.section}` : ''}
                      </Link>
                      {from && to && (
                        <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                          {from} → {to}
                        </span>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <div className="card p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
                <BookOpen className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Subjects Assigned</p>
                <p className="text-2xl font-bold text-gray-900">{assignedSubjects.length}</p>
              </div>
            </div>
          </div>
          <div className="card p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <UserRound className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Class Sections Covered</p>
                <p className="text-2xl font-bold text-gray-900">{classSections.length}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              {classSections.length > 0 ? classSections.map((label) => (
                <span key={label} className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                  {label}
                </span>
              )) : (
                <span className="text-sm text-gray-400">No class sections linked yet</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Assigned Subjects</h3>
            <p className="text-sm text-gray-500 mt-1">Every subject currently linked to this teacher, including the class section it belongs to.</p>
          </div>
        </div>

        {assignedSubjects.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <BookOpen className="w-10 h-10 mx-auto mb-3 text-gray-200" />
            <p className="text-sm">No subjects are assigned to this teacher yet.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-5">
            {assignedSubjects.map((subject) => (
              <div key={subject._id || subject.id} className="rounded-2xl border border-gray-100 bg-gray-50/70 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h4 className="font-semibold text-gray-900">{subject.name}</h4>
                    {subject.code && <p className="text-xs text-gray-400 mt-1">{subject.code}</p>}
                  </div>
                  <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-gray-500 shadow-sm">
                    {getClassLabel(getSubjectClass(subject))}
                  </span>
                </div>
                {subject.description && (
                  <p className="text-sm text-gray-500 mt-3 line-clamp-3">{subject.description}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
